import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import { BucketBadge, type Bucket } from '@/components/BucketBadge';
import { cn } from '@/lib/cn';
import { useApiQuery, type MembersResponse } from '@/lib/api';

const BUCKETS: Bucket[] = ['Engaged', 'Watching', 'AtRisk', 'Slipping'];
const LABELS: Record<Bucket, string> = {
  Engaged: 'Engaged',
  Watching: 'Watching',
  AtRisk: 'At Risk',
  Slipping: 'Slipping',
};

const TENURE_LABELS: Record<string, string> = {
  Year1: 'First year',
  Years2to5: '2–5 years',
  Years6Plus: '6+ years',
};

export function MemberList() {
  const [params, setParams] = useSearchParams();
  const bucket = params.get('bucket') as Bucket | null;
  const search = params.get('search') ?? '';

  // Keep typing responsive without a request per keystroke.
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (draft) next.set('search', draft);
      else next.delete('search');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const path = useMemo(() => {
    const qs = new URLSearchParams({ limit: '100' });
    if (bucket) qs.set('bucket', bucket);
    if (search) qs.set('search', search);
    return `/api/members?${qs.toString()}`;
  }, [bucket, search]);

  const { data, loading, error } = useApiQuery<MembersResponse>(path);
  const items = data?.items ?? [];

  const setBucket = (b: Bucket | null) => {
    const next = new URLSearchParams(params);
    if (b) next.set('bucket', b);
    else next.delete('bucket');
    setParams(next, { replace: true });
  };

  return (
    <div className="container mx-auto max-w-7xl px-6 py-10">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Members</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Member list</h1>
        <p className="mt-2 text-muted-foreground">
          {bucket
            ? `${LABELS[bucket]} members, least engaged first.`
            : 'Every member, with their current engagement score.'}
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search by name…"
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none ring-primary/30 placeholder:text-muted-foreground focus:ring-2"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={bucket === null} onClick={() => setBucket(null)}>
            All
          </FilterChip>
          {BUCKETS.map((b) => (
            <FilterChip key={b} active={bucket === b} onClick={() => setBucket(b)}>
              {LABELS[b]}
            </FilterChip>
          ))}
        </div>

        {(bucket || search) && (
          <button
            type="button"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        )}
      </div>

      {error && (
        <p className="mt-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>
      )}

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-5 py-3 text-left font-medium">Member</th>
              <th className="px-5 py-3 text-left font-medium">Tenure</th>
              <th className="px-5 py-3 text-left font-medium">Last seen</th>
              <th className="px-5 py-3 text-left font-medium">Bucket</th>
              <th className="px-5 py-3 text-right font-medium">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            )}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">
                  {search ? `No members matching "${search}".` : 'No members found.'}
                </td>
              </tr>
            )}

            {!loading &&
              items.map((m) => (
                <tr key={m.profileId} className="hover:bg-muted/30">
                  <td className="px-5 py-3.5">
                    <Link
                      to={`/members/${m.profileId}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {m.legalName ?? m.profileId}
                    </Link>
                    {m.city && (
                      <p className="text-xs text-muted-foreground">
                        {m.city}
                        {m.state ? `, ${m.state}` : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">
                    {m.tenureBucket ? (TENURE_LABELS[m.tenureBucket] ?? m.tenureBucket) : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">
                    {m.recencyDays === null || m.recencyDays === undefined ? (
                      <span title="No usable activity date in the source data">unknown</span>
                    ) : (
                      `${m.recencyDays}d ago`
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {m.bucket ? <BucketBadge bucket={m.bucket} /> : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-right font-medium tabular-nums">
                    {m.score ?? '—'}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && items.length > 0 && (
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Showing {items.length.toLocaleString()} member{items.length === 1 ? '' : 's'}
          {data?.nextCursor && ' — narrow with a filter or a search to see the rest'}
        </p>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}
